import { useEffect, useMemo, useState } from 'react'
import { Check, LoaderCircle, Sparkles, X } from 'lucide-react'
import './workspace-upgrade-dialog.css'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { fetchBillingOptions } from '@/features/billing/services/billing-service'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import { useSettingsStore } from '@/features/settings/stores/settings-store'
import { formatBillingCycleLabel } from '@/features/billing/utils/membership-labels'
import type { BillingPlan } from '@/features/billing/types'
import type { AuthMembership } from '@/features/auth/types'
import { cn } from '@/shared/ui/cn'

function isPaidPlan(plan: BillingPlan) {
  return !plan.plan_code.includes('free') && Number(plan.price) > 0
}

function formatBytes(bytes: number) {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(0)}GB`
  return `${Math.round(bytes / 1024 ** 2)}MB`
}

function planHighlights(plan: BillingPlan) {
  const storage = plan.storagePolicy
    ? [`单文件上传最大 ${formatBytes(plan.storagePolicy.maxUploadBytes)}`, `云端素材空间 ${formatBytes(plan.storagePolicy.assetStorageBytes)}`, `云端作品保存 ${plan.storagePolicy.projectRetentionDays} 天`, `单次切片最长 ${Math.floor(plan.storagePolicy.maxClippingInputSeconds / 60)} 分钟`]
    : ['存储权益以当前套餐配置为准']
  return [...storage, '本地编辑项目永久保留，不占云端空间', ...plan.feature_flags]
}

function formatPlanPrice(price: BillingPlan['price']) {
  const numericPrice = Number(price)
  return Number.isFinite(numericPrice)
    ? numericPrice.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    : price
}

export function WorkspaceUpgradeDialog({
  open,
  onOpenChange,
  membership,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  membership: AuthMembership | null | undefined
}) {
  const openCheckout = useMembershipDialogStore((state) => state.openDialog)
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [allPlans, setAllPlans] = useState<BillingPlan[]>([])
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const currentPlanCode = membership?.planCode ?? null
  const showInterfaceAnimations = useSettingsStore((state) => state.showInterfaceAnimations)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError('')
    void fetchBillingOptions()
      .then((payload) => {
        if (!cancelled) {
          setAllPlans(payload.plans)
          setPlans(payload.plans.filter(isPaidPlan).sort((a, b) => a.sort_order - b.sort_order))
        }
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError instanceof Error ? requestError.message : '套餐加载失败，请稍后重试。')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [open])

  const visiblePlans = useMemo(() => plans.filter((plan) => plan.billing_cycle === billingCycle), [billingCycle, plans])
  const hasYearly = plans.some((plan) => plan.billing_cycle === 'yearly')
  const freePlan = useMemo(() => allPlans.find((plan) => plan.plan_code.includes('free')) ?? null, [allPlans])

  const choosePlan = (plan: BillingPlan) => {
    onOpenChange(false)
    openCheckout({ preferredPlanCode: plan.plan_code, surface: 'workspace' })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className={cn('workspace-upgrade-dialog flex max-h-[calc(100dvh-1.5rem)] max-w-[800px] flex-col overflow-hidden border-[#2a3028] bg-[#101210] p-0 text-[#f5f7f2] shadow-[0_28px_80px_rgba(0,0,0,.62)] sm:rounded-[18px]', !showInterfaceAnimations && 'workspace-upgrade-dialog--motion-disabled')}>
        <div className="shrink-0 border-b border-[#252b24] px-5 py-5 sm:px-7 sm:py-6">
          <div className="flex items-start justify-between gap-5 pr-7">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-bold tracking-[.14em] text-[#91e653]"><Sparkles className="size-3.5" />JIAN MEMBERSHIP</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">选择适合你的创作产能</h2>
              <p className="mt-1.5 max-w-2xl text-sm leading-6 text-[#9aa397]">当前：{membership?.planName ?? 'Free'}。选择套餐后才会进入安全支付页面。</p>
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="-mr-2 -mt-1 rounded-md p-2 text-[#9aa397] transition-colors hover:bg-[#1c221b] hover:text-white" aria-label="关闭升级弹窗"><X className="size-5" /></button>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
          <section className="mx-auto w-full max-w-[720px]">
            <div className="mb-5 flex flex-col items-center gap-2">
              <div className="inline-flex rounded-full border border-[#313a2e] bg-[#151915] p-1" role="tablist" aria-label="订阅周期">
                <button type="button" role="tab" aria-selected={billingCycle === 'monthly'} onClick={() => setBillingCycle('monthly')} className={cn('rounded-full px-5 py-2 text-sm font-semibold transition-colors', billingCycle === 'monthly' ? 'bg-[#91e653] text-[#152012] shadow-[0_2px_10px_rgba(145,230,83,.2)]' : 'text-[#a6afa3] hover:text-white')}>月付</button>
                <button type="button" role="tab" aria-selected={billingCycle === 'yearly'} disabled={!hasYearly} onClick={() => setBillingCycle('yearly')} className={cn('rounded-full px-5 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40', billingCycle === 'yearly' ? 'bg-[#91e653] text-[#152012] shadow-[0_2px_10px_rgba(145,230,83,.2)]' : 'text-[#a6afa3] hover:text-white')}>年付</button>
              </div>
              <span className={cn('text-xs', billingCycle === 'yearly' ? 'font-medium text-[#91e653]' : 'text-[#8d968b]')}>{billingCycle === 'yearly' ? '年付更划算' : '随时按需升级'}</span>
            </div>
            {loading ? <div className="grid min-h-56 place-items-center text-sm text-[#a6afa3]"><span className="inline-flex items-center gap-2"><LoaderCircle className="size-4 animate-spin" />正在加载可用套餐…</span></div> : null}
            {error ? <div className="rounded-xl border border-[#5a322d] bg-[#291714] px-4 py-3 text-sm text-[#ffb4a9]">{error}</div> : null}
            {!loading && !error ? <div className="grid gap-3 sm:grid-cols-2">
              {visiblePlans.map((plan) => {
                const isCurrent = plan.plan_code === currentPlanCode
                const highlights = planHighlights(plan)
                const isStudio = plan.plan_code.includes('studio')
                return <article key={plan.plan_code} className={cn('workspace-upgrade-dialog__plan flex flex-col rounded-xl border p-4 transition-colors', isStudio ? 'workspace-upgrade-dialog__plan--studio' : 'workspace-upgrade-dialog__plan--creator', isCurrent ? 'border-[#91e653] bg-[#182016]' : isStudio ? 'border-[#486453] bg-[#151915]' : 'border-[#2a3028] bg-[#151915] hover:border-[#4c5c48]')}>
                  <div className="flex items-start justify-between gap-2"><div><h3 className="text-base font-semibold">{plan.plan_name}</h3><div className="mt-2 flex items-end gap-1.5"><span className={cn('text-[1.9rem] font-semibold leading-none tracking-[-.055em]', isStudio ? 'text-[#79e6ff]' : 'text-[#a8f96c]')}>¥{formatPlanPrice(plan.price)}</span><span className="mb-0.5 text-xs font-medium text-[#9ea79b]">/{plan.billing_cycle === 'yearly' ? '年' : '月'}</span></div><p className="mt-1.5 text-[11px] text-[#788175]">{formatBillingCycleLabel(plan)}</p></div>{isCurrent ? <span className="rounded-full bg-[#91e653] px-2 py-1 text-[10px] font-semibold text-[#152012]">当前套餐</span> : null}</div>
                  <button type="button" onClick={() => choosePlan(plan)} className={cn('mt-4 inline-flex h-9 items-center justify-center rounded-full px-4 text-xs font-semibold transition-colors', isCurrent ? 'bg-[#91e653] text-[#152012] hover:bg-[#7ed446]' : plan.plan_code.includes('studio') ? 'bg-[#f5f7f2] text-[#152012] hover:bg-white' : 'border border-[#465044] text-[#f5f7f2] hover:border-[#91e653] hover:text-[#91e653]')}>
                    {isCurrent ? '续费当前套餐' : `选择 ${plan.plan_name}`}
                  </button>
                  <div className="mt-4 space-y-2 border-t border-[#2a3028] pt-3.5 text-xs leading-5 text-[#bcc4b9]">{highlights.map((item) => <p key={item} className="flex gap-2"><Check className="mt-0.5 size-3.5 shrink-0 text-[#91e653]" />{item}</p>)}</div>
                </article>
              })}
              {!visiblePlans.length ? <p className="col-span-full py-12 text-center text-sm text-[#a6afa3]">当前没有可购买套餐。</p> : null}
            </div> : null}
            {freePlan?.storagePolicy ? <p className="mt-5 text-center text-xs text-[#8d968b]">Free：单文件 {formatBytes(freePlan.storagePolicy.maxUploadBytes)} · 云端素材 {formatBytes(freePlan.storagePolicy.assetStorageBytes)} · 云端作品保存 {freePlan.storagePolicy.projectRetentionDays} 天</p> : null}
          </section>
        </div>
        <p className="border-t border-[#252b24] px-6 py-3 text-center text-xs leading-5 text-[#8d968b]">本地项目由你的设备保存，不设有效期；只有上传、云端渲染、分享或跨设备同步产生的云端副本适用云端保存期限。</p>
      </DialogContent>
    </Dialog>
  )
}
